import { ApplicationRef, inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { DxFormComponent } from "devextreme-angular/ui/form";
import DataSource from "devextreme/data/data_source";
import { forkJoin, lastValueFrom } from "rxjs";
import { ConfigurationBackend } from "src/app/backend/api/configuration.backend";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { ReminderTemplateBackend } from "src/app/backend/api/reminder-template.backend";
import { ReminderBackend } from "src/app/backend/api/reminder.backend";
import { AppointmentConfiguration } from "src/app/backend/models/appointment-configuration";
import { Appointment } from "src/app/backend/models/appointment/appointment";
import { Reminder } from "src/app/backend/models/reminder";
import { Tenant } from "src/app/backend/models/tenant/tenant";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { createReminderNoUI } from "src/app/modules/bootstrap/pms-ui/functions/create-new-reminder.function";
import { DocumentService } from "src/app/services/document.service";
import { RegionService } from "src/app/services/feature-access-control/region.service";
import { PopupService } from "src/app/services/popup/popup.service";
import { ToastService } from "src/app/services/toast.service";
import {
  getCustomerNominative,
  getPrefixNumber
} from "src/app/support/customer";
import { dateUtil } from "src/app/support/date-util";
import { omitUndefinedAndNullProperties } from "src/app/support/object";
import {
  buildPhoneNumberWithPrefix,
  getDefaultPrefixPhone,
  getPhoneNumber,
  getRegionCodePhoneNumber,
  validatePhone
} from "src/app/support/phone";
import { getPublicFileSrcById } from "src/app/support/public-file";
import { hasText } from "src/app/support/text";
import { sntTranslate } from "src/app/support/translate";
import { TEMPLATE_APPOINTMENT_REMINDER_TOKEN } from "src/app/tokens/template_appointment_reminder.token";
import { environment } from "src/environments/environment";

type Input = {
  appointment: Appointment;
  expireDate: string;
  tenant: Tenant;
  resources: string[];
  summary: string | undefined;
};

type ModalSendNotificationResult = {
  transport: "email" | "unknown";
  phone: string | undefined;
  email: string | undefined;
};

type CreateReminderFunctionResult = {
  transport: "email" | "unknown";
  email?: string;
  phone?: string;
};

@Injectable()
export class CreateReminderFunctionService {
  private readonly applicationRef = inject(ApplicationRef);
  private readonly bootstrapService = inject(BootstrapService);
  private readonly customerBackend = inject(CustomerBackend);
  private readonly documentService = inject(DocumentService);
  private readonly patientBackend = inject(PetBackend);
  private readonly popupService = inject(PopupService);
  private readonly reminderTemplateBackend = inject(ReminderTemplateBackend);
  private readonly reminderBackend = inject(ReminderBackend);
  private readonly configurationBackend = inject(ConfigurationBackend);
  private readonly toastService = inject(ToastService);
  private readonly regionService = inject(RegionService);

  private readonly templateAppointmentReminderFactory = inject(
    TEMPLATE_APPOINTMENT_REMINDER_TOKEN
  );

  async execute(input: Input): Promise<CreateReminderFunctionResult | null> {
    if (input.appointment.pattern) {
      /**
       * https://team-1622446351368.atlassian.net/browse/SNT-7408
       * La creazione dei reminder è stata disabilitata nel caso di appuntamenti ricorrenti
       * in attesa dell'implementazione della feature lato BE
       */
      return null;
    }

    const appointmentConfiguration = await lastValueFrom(
      this.configurationBackend.loadCalendar()
    );

    const [, userResponse] = await Promise.all([
      /*
       * elimino tutti i reminder precedentementi associati a questo appuntamento
       * catch del reject per continuare l'operazione anche nel caso fallisca il DELETE di qualche reminder
       */
      this.deleteAllRemindersWithThisAppointment({
        appointmentId: input.appointment.id
      }).catch(() => {}),
      // verifico se l'utente vuole notificare ora dell'appuntamento
      this.askUserIfWantNotifyContactNow(
        input.appointment,
        appointmentConfiguration
      )
    ]);

    if (
      !userResponse ||
      !!appointmentConfiguration.appointment?.reminderDisabled
    ) {
      return null;
    }

    const notificationDate = dateUtil(input.expireDate)
      .add(-1, "day")
      .toISOString();
    const postingDate = dateUtil().format("DD/MM/YYYY");

    const subject = sntTranslate(
      marker(
        "Promemoria: appuntamento presso tenantName previsto per il date alle time"
      ),
      {
        tenantName: input.tenant.name,
        date: dateUtil(input.expireDate).format("L"),
        time: dateUtil(input.expireDate).format("LT")
      }
    );

    const customerId = input.appointment.customer.id;

    await createReminderNoUI(
      this.bootstrapService.getCurrentMember()!,
      this.bootstrapService.getCurrentTenant()!,
      {
        channel: userResponse.transport,
        postingDate,
        expireDate: input.expireDate,
        expireDateCustom: undefined,
        notificationDate,
        patientId: undefined,
        patientName: undefined,
        type: "appointment",
        recurring: false,
        subject,
        templateId: this.templateAppointmentReminderFactory(),
        [`customer-${customerId}`]: customerId,
        resources: input.resources
      },
      {
        applicationRef: this.applicationRef,
        bootstrapService: this.bootstrapService,
        customerBackend: this.customerBackend,
        documentService: this.documentService,
        patientBackend: this.patientBackend,
        popupService: this.popupService,
        reminderTemplateBackend: this.reminderTemplateBackend,
        reminedBackend: this.reminderBackend,
        toastService: this.toastService
      },
      {
        silently: true
      },
      omitUndefinedAndNullProperties({
        number: input.appointment.number,
        customerName: getCustomerNominative(input.appointment.customer),
        patientName: input.appointment.patient?.name,
        clinicLogo: <string>(
          getPublicFileSrcById(input?.tenant?.logo, { useFallback: true })
        ),
        clinicName: input.tenant.name,
        clinicAddress: input.tenant.address?.address || "",
        clinicCity: input.tenant.address?.city || "",
        clinicState: input.tenant.address?.state || "",
        clinicZipCode: input.tenant.address?.zipCode || "",
        clinicPhone: input.tenant.phone || "",
        summary: input.summary,
        startDate: dateUtil(input.expireDate).format("L"),
        startTime: dateUtil(input.expireDate).format("LT"),
        endDate: dateUtil(input.appointment.endAt).format("L"),
        endTime: dateUtil(input.appointment.endAt).format("LT"),
        locale: this.regionService.getLocale()
      })
    );

    return {
      transport: userResponse.transport,
      email: userResponse.email,
      phone: userResponse.phone
    };
  }

  private async deleteAllRemindersWithThisAppointment({
    appointmentId
  }: {
    appointmentId: string;
  }): Promise<void> {
    const reminders = await lastValueFrom(
      this.reminderBackend.listByResource(appointmentId)
    );

    if (reminders && reminders.length > 0) {
      await lastValueFrom(
        forkJoin(
          reminders.map((reminder: Reminder) => {
            return this.reminderBackend.deleteSilently(reminder.id);
          })
        )
      );
    }
  }

  private askUserIfWantNotifyContactNow(
    appointment: Appointment,
    appointmentConfiguration: AppointmentConfiguration
  ): Promise<ModalSendNotificationResult | null> {
    return new Promise<ModalSendNotificationResult | null>(async (res, rej) => {
      if (appointment?.internal || !appointment.customer) {
        res(null);
        return;
      }

      let dxForm: DxFormComponent | undefined;

      await this.popupService.createEntity<{
        transport: "email" | "unknown" | null;
        email: string;
        phone: string;
        prefixPhone: string;
      }>({
        title: sntTranslate(marker("Calendario")),
        confirmText: sntTranslate(marker("Conferma")),
        rejectText: sntTranslate(marker("Annulla")),
        modalConfig: {
          height: 300,
          width: 650,
          dialog: true
        },
        initialFormData: {
          transport: <"email" | "unknown">appointment.transport,
          email: appointment.customer?.email,
          phone: getPhoneNumber(
            appointment.customer?.phone!,
            this.bootstrapService
          ),
          prefixPhone:
            getRegionCodePhoneNumber(
              appointment.customer?.phone!,
              this.bootstrapService
            )?.toLowerCase() ??
            getDefaultPrefixPhone(this.bootstrapService).toLowerCase()
        },
        onFormReady: (dxArgs: { dxForm: DxFormComponent }) => {
          dxForm = dxArgs.dxForm;
        },
        formItems: [
          {
            template: () =>
              `<span class="w-full text-base">${sntTranslate(
                marker("Vuoi inviare l’appuntamento al cliente?")
              )}</span>`
          },
          {
            dataField: "transport",
            editorType: "dxRadioGroup",
            label: {
              text: sntTranslate(marker("Canale")),
              location: "top",
              showColon: false
            },
            editorOptions: {
              dataSource: [
                {
                  value: "email",
                  display: sntTranslate(marker("Email"))
                },
                {
                  value: "unknown",
                  display: sntTranslate(marker("Whatsapp"))
                }
              ],
              valueExpr: "value",
              displayExpr: "display",
              layout: "horizontal",
              onValueChanged: () => {
                dxForm!.instance.itemOption("email", {
                  editorOptions: {
                    readOnly: dxForm!.formData?.transport !== "email"
                  },
                  validationRules:
                    dxForm!.formData?.transport === "email"
                      ? [
                          { type: "required" },
                          {
                            type: "email",
                            message: sntTranslate(
                              marker("Il formato del email non è corretto")
                            )
                          }
                        ]
                      : []
                });

                dxForm!.instance.itemOption("phone", {
                  editorOptions: {
                    readOnly: dxForm!.formData?.transport !== "unknown"
                  },
                  validationRules:
                    dxForm!.formData?.transport === "unknown"
                      ? [{ type: "required" }]
                      : []
                });

                dxForm!.instance.itemOption("prefixPhone", {
                  editorOptions: {
                    readOnly: dxForm!.formData?.transport !== "unknown"
                  },
                  validationRules:
                    dxForm!.formData?.transport === "unknown"
                      ? [{ type: "required" }]
                      : []
                });
              }
            },
            validationRules: [{ type: "required" }]
          },
          {
            dataField: "email",
            label: {
              text: sntTranslate(marker("Email")),
              location: "top",
              showColon: false
            },
            editorOptions: {
              readOnly: appointment?.transport !== "email"
            },
            validationRules: [
              {
                type: "email",
                message: sntTranslate(
                  marker("Il formato del email non è corretto")
                )
              }
            ]
          },
          {
            itemType: "group",
            colCount: 5,
            label: {
              text: "Telefono",
              location: "top",
              showColon: false
            },
            items: [
              {
                colSpan: 1,
                dataField: "prefixPhone",
                editorType: "dxSelectBox",
                label: { visible: false },
                editorOptions: {
                  dataSource: new DataSource({
                    store: getPrefixNumber()
                  }),
                  valueExpr: "value",
                  displayExpr: "display",
                  layout: "horizontal",
                  searchEnabled: true,
                  placeholder: sntTranslate(marker("Prefisso...")),
                  readOnly: appointment.transport !== "unknown"
                }
              },
              {
                dataField: "phone",
                colSpan: 4,
                editorOptions: {
                  readOnly: appointment.transport !== "unknown"
                },
                label: { visible: false }
              }
            ]
          },
          {
            visible: !appointmentConfiguration.appointment?.reminderDisabled,
            itemType: "empty"
          },
          {
            visible: !appointmentConfiguration.appointment?.reminderDisabled,
            template: () =>
              `<span class="w-full text-slate-400 italic text-sm">${sntTranslate(
                marker(
                  "Ricorderemo l’appuntamento al cliente il giorno prima. Il promemoria sarà inviato sullo stesso canale dell’appuntamento."
                )
              )}</span>`
          }
        ],
        onCancel: async () => {
          res(null);
        },
        create: async (data) => {
          switch (data.transport) {
            case "email": {
              if (!data.email) {
                rej(sntTranslate(marker("Email mancante")));
                return;
              }

              res(<ModalSendNotificationResult>{
                transport: "email",
                email: data.email
              });

              break;
            }
            case "unknown": {
              if (!data.phone) {
                rej(sntTranslate(marker("Numero di telefono mancante")));
                return;
              }

              if (!hasText(data.prefixPhone)) {
                rej(sntTranslate(marker("Prefisso mancante")));
                return;
              }

              if (
                !validatePhone(
                  {
                    phone: data.phone,
                    countryCode: data.prefixPhone
                  },
                  this.bootstrapService
                )
              ) {
                rej(sntTranslate(marker("Numero di telefono errato")));
                return;
              }

              const formPhone = buildPhoneNumberWithPrefix(
                {
                  phone: data.phone,
                  countryCode: data.prefixPhone
                },
                this.bootstrapService
              );

              res(<ModalSendNotificationResult>{
                transport: "unknown",
                phone: formPhone
              });

              break;
            }
          }
        }
      });
    });
  }
}
